// Whether enough has been tried before substituted service.
//
// Substituted service leaves the papers with someone else. It is only proper
// after reasonable diligence — genuinely trying to serve the person themselves —
// and a Proof that claims it without the attempts behind it is a Proof that can
// be attacked. So the admin is warned, told exactly how far short they are, and
// allowed to proceed anyway with a written reason on the record.
//
// NOT A BLOCK. The person recording the service knows things this program does
// not: a subject who has plainly moved, a gate that will never open, a client
// deadline. The override exists because the judgement is theirs; the audit
// exists because it should be visible that they made it.
//
// PURE. No clock, no network. The attempts are passed in — and they must be the
// PERSISTED ones. A count from the form in front of the admin is not evidence
// of anything: it is what they have typed, not what happened.
//
// Shared by the browser (warning) and the server (enforcement) so the two can
// never disagree about what "met" means.

// California's standard practice, and what this project uses everywhere: three
// genuine attempts before substituting. Configurable, because "configured" is
// how the requirement was described — but with a real default so a missing
// config never silently means zero.
const DEFAULT_REQUIRED_ATTEMPTS = 3;

// An attempt that already IS a substituted service cannot be part of the
// diligence that justifies it, and neither can a drop-serve.
const NON_QUALIFYING_OUTCOMES = ["served_substitute", "served_posting", "drop_served"];

// Kari's rule (Nov 2): before substituted service or posting, the attempts
// (on different days) must include at least one BEFORE 11:00 AM and at least
// one at 6:00 PM OR LATER. Midday attempts count toward the total but are not
// required.
const AB747_EFFECTIVE = "2026-10-27";
const AB747_WINDOWS = ["early", "late"];
function timeWindowOf(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ""));
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return mins < 11 * 60 ? "early" : mins >= 18 * 60 ? "late" : "midday";
}
function ab747Active(todayIso) {
  const d = String(todayIso || new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date())).slice(0, 10);
  return d >= AB747_EFFECTIVE;
}

function requiredAttemptsFrom(config) {
  const raw = config ? config.substituted_service_min_attempts : undefined;
  // ABSENT is not zero. Number(null) is 0 and Number("") is 0, so a missing or
  // empty setting would otherwise read as "no attempts required" — the one
  // wrong answer this function must never give.
  if (raw == null || raw === "") return DEFAULT_REQUIRED_ATTEMPTS;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : DEFAULT_REQUIRED_ATTEMPTS;
}

// The attempts that count, for ONE service target.
//
// Three things are excluded, each for its own reason:
//   * another target's attempts — a different person, a different diligence
//   * an attempt that is itself a substituted/drop service
//   * the same attempt seen twice (by id), which would otherwise let a repeated
//     read inflate the count
//
// And what remains is counted BY DAY: three knocks on one afternoon is one
// afternoon's effort, not three days of diligence.
function qualifyingAttempts(attempts, targetId) {
  const seen = new Set();
  const rows = [];
  for (const a of Array.isArray(attempts) ? attempts : []) {
    if (!a) continue;
    if (targetId != null && String(a.target_id) !== String(targetId)) continue;
    if (NON_QUALIFYING_OUTCOMES.includes(String(a.outcome))) continue;
    const id = a.id == null ? null : String(a.id);
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    rows.push(a);
  }
  return rows;
}

// The distinct calendar days those attempts fall on. An attempt with no date
// cannot prove a day, so it is not counted as one.
function qualifyingDays(rows) {
  const days = new Set();
  for (const a of rows || []) {
    const d = a && a.attempt_date ? String(a.attempt_date).slice(0, 10) : null;
    if (d) days.add(d);
  }
  return [...days].sort();
}

// Informational only. The project has never encoded a time-of-day rule, so this
// reports what the attempts look like without gating on it — a caller can show
// it, and nothing refuses a save because of it.
function timeSpread(rows) {
  const hours = new Set();
  for (const a of rows || []) {
    const t = a && a.attempt_time ? String(a.attempt_time).slice(0, 2) : null;
    if (t && /^\d\d$/.test(t)) hours.add(Number(t));
  }
  const list = [...hours].sort((x, y) => x - y);
  return { hours: list, varied: list.length > 1 };
}

// The whole picture, from persisted attempts.
//
//   { required, qualifying, days, remaining, met, timeVariation }
//
// `qualifying` is the number of DAYS with a qualifying attempt, because that is
// what the rule is about. `remaining` never goes negative.
function evaluateDiligence(attempts, opts) {
  const o = opts || {};
  const required = o.required != null ? Number(o.required) : requiredAttemptsFrom(o.config);
  const rows = qualifyingAttempts(attempts, o.targetId);
  const days = qualifyingDays(rows);
  const qualifying = days.length;
  const remaining = Math.max(0, required - qualifying);
  const windows = Array.from(new Set(rows.map((a) => timeWindowOf(a && a.attempt_time)).filter(Boolean)));
  const ab747 = o.ab747 != null ? !!o.ab747 : ab747Active(o.today);
  const missingWindows = AB747_WINDOWS.filter((w) => !windows.includes(w));
  const timesOk = !ab747 || missingWindows.length === 0;
  return {
    required,
    qualifying,
    attemptRows: rows.length,
    days,
    remaining,
    met: remaining === 0 && timesOk,
    timeVariation: timeSpread(rows),
    ab747,
    timeWindows: windows,
    timesOk,
    missingWindows: ab747 ? missingWindows : [],
  };
}

// Is this save a substituted service? Manner and outcome are both checked
// because either can carry it depending on where the save came from.
function isSubstitutedService(attempt) {
  const a = attempt || {};
  return String(a.manner_attempted) === "substitute" || String(a.outcome) === "served_substitute"
    || String(a.manner_attempted) === "posting" || String(a.outcome) === "served_posting";
}

// What the admin is told. Exact, and about attempts rather than jargon.
function warningMessage(evaluation) {
  const e = evaluation || {};
  if (e.met) return null;
  const n = e.remaining;
  if (n > 0) return `Due diligence has not been met. ${n} additional ${n === 1 ? "attempt is" : "attempts are"} required before substituted service or posting.`;
  const need = (e.missingWindows || []).map((w) => (w === "early" ? "an attempt before 11:00 AM" : "an attempt at 6:00 PM or later"));
  return `Before substituted service or posting you need at least one attempt before 11:00 AM and at least one at 6:00 PM or later. Still needed: ${need.join(" and ")}.`;
}

// An override has to be a real decision with a real reason behind it.
//   { ok } | { ok: false, reason }
function validateOverride(override) {
  const o = override || {};
  if (o.used !== true) return { ok: false, reason: "not_requested" };
  const explanation = String(o.reason == null ? "" : o.reason).trim();
  if (!explanation) return { ok: false, reason: "explanation_required" };
  return { ok: true, explanation };
}

// The columns recorded on the resulting attempt. Written only when an override
// was actually used — a normal substituted service carries none of this.
function overrideColumns(evaluation, explanation, adminName, nowIso) {
  const e = evaluation || {};
  return {
    diligence_override_used: true,
    diligence_override_reason: explanation,
    diligence_required_attempts: e.required == null ? null : Number(e.required),
    diligence_qualifying_attempts_at_override: e.qualifying == null ? null : Number(e.qualifying),
    diligence_remaining_attempts_at_override: e.remaining == null ? null : Number(e.remaining),
    diligence_overridden_at: nowIso || null,
    diligence_overridden_by: adminName || null,
  };
}

module.exports = {
  DEFAULT_REQUIRED_ATTEMPTS,
  NON_QUALIFYING_OUTCOMES,
  requiredAttemptsFrom,
  qualifyingAttempts,
  qualifyingDays,
  timeSpread,
  evaluateDiligence,
  AB747_EFFECTIVE,
  timeWindowOf,
  ab747Active,
  isSubstitutedService,
  warningMessage,
  validateOverride,
  overrideColumns,
};
